// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WEDDING_CONFIG } from '../../config';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [rsvps, setRsvps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    attending: 0,
    notAttending: 0,
    totalGuests: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      // Get RSVPs
      const { getAllRSVPs } = await import('../../services/rsvpService');
      const rsvpData = await getAllRSVPs();
      if (rsvpData.success) {
        setRsvps(rsvpData.data);
        setStats(rsvpData.stats);
      }

      // Get Photos from Cloudinary service
      const { getAllPhotos } = await import('../../services/cloudinaryService');
      const photoData = await getAllPhotos();
      if (photoData.success) {
        setPhotos(photoData.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Fetch error:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { adminLogout } = await import('../../services/authService');
      await adminLogout();
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      navigate('/admin/login');
    }
  };

  const deleteRSVP = async (id) => {
    if (!window.confirm('Are you sure you want to delete this RSVP?')) return;
    try {
      const { deleteRSVP: deleteRSVPService } = await import('../../services/rsvpService');
      const result = await deleteRSVPService(id);
      if (result.success) {
        alert('RSVP deleted successfully!');
        fetchData();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete RSVP');
    }
  };

  const approvePhoto = async (id) => {
    try {
      const { approvePhoto: approvePhotoService } = await import('../../services/cloudinaryService');
      const result = await approvePhotoService(id);
      if (result.success) {
        alert('Photo approved!');
        fetchData();
      }
    } catch (error) {
      console.error('Approve error:', error);
      alert('Failed to approve photo');
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      const photo = photos.find(p => p._id === id);
      const { deletePhoto: deletePhotoService } = await import('../../services/cloudinaryService');
      const result = await deletePhotoService(id, photo?.publicId);
      if (result.success) {
        alert('Photo deleted!');
        fetchData();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete photo');
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Name', 'Attendance', 'Guests', 'Message', 'Submitted'],
      ...rsvps.map(rsvp => [
        rsvp.name,
        rsvp.attendance,
        rsvp.guests,
        rsvp.message || '',
        new Date(rsvp.submittedAt).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wedding-rsvps.csv';
    a.click();
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loading}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>Wedding Dashboard</h1>
            <p style={styles.subtitle}>{localStorage.getItem('adminEmail')}</p>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
          </button>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{...styles.tab, ...(activeTab === 'overview' ? styles.activeTab : {})}}
            onClick={() => setActiveTab('overview')}
          >
          Overview
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'rsvps' ? styles.activeTab : {})}}
            onClick={() => setActiveTab('rsvps')}
          >
          RSVPs ({stats.total})
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'photos' ? styles.activeTab : {})}}
            onClick={() => setActiveTab('photos')}
          >
          Photos ({photos.length})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div style={styles.statsGrid}>
              <div style={{...styles.statCard, borderTop: `4px solid ${WEDDING_CONFIG.colors.primary}`}}>
                <h3 style={{...styles.statNumber, color: WEDDING_CONFIG.colors.primary}}>
                  {stats.total}
                </h3>
                <p style={styles.statLabel}>TOTAL RESPONSES</p>
              </div>
              <div style={{...styles.statCard, borderTop: '4px solid #28a745'}}>
                <h3 style={{...styles.statNumber, color: '#28a745'}}>
                  {stats.attending}
                </h3>
                <p style={styles.statLabel}>ATTENDING</p>
              </div>
              <div style={{...styles.statCard, borderTop: '4px solid #dc3545'}}>
                <h3 style={{...styles.statNumber, color: '#dc3545'}}>
                  {stats.notAttending}
                </h3>
                <p style={styles.statLabel}>NOT ATTENDING</p>
              </div>
              <div style={{...styles.statCard, borderTop: `4px solid ${WEDDING_CONFIG.colors.secondary}`}}>
                <h3 style={{...styles.statNumber, color: WEDDING_CONFIG.colors.secondary}}>
                  {stats.totalGuests}
                </h3>
                <p style={styles.statLabel}>TOTAL GUESTS</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={styles.contentCard}>
              <h2 style={styles.cardTitle}>Quick Actions</h2>
              <div style={styles.quickActionsGrid}>
                <button onClick={exportToCSV} style={styles.quickActionButton}>
                  <span style={styles.quickActionIcon}></span>
                  <span style={styles.quickActionText}>Export RSVPs to CSV</span>
                </button>
                <button onClick={() => setActiveTab('rsvps')} style={styles.quickActionButton}>
                  <span style={styles.quickActionIcon}></span>
                  <span style={styles.quickActionText}>View All RSVPs</span>
                </button>
                <button onClick={() => setActiveTab('photos')} style={styles.quickActionButton}>
                  <span style={styles.quickActionIcon}></span>
                  <span style={styles.quickActionText}>Manage Photos</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* RSVPs Tab */}
        {activeTab === 'rsvps' && (
          <div style={styles.contentCard}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>All RSVP Responses</h2>
              <button onClick={exportToCSV} style={styles.exportButton}>
              Export to CSV
              </button>
            </div>

            {rsvps.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyIcon}></p>
                <p>No RSVPs submitted yet</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>NAME</th>
                      <th style={styles.th}>ATTENDANCE</th>
                      <th style={styles.th}>GUESTS</th>
                      <th style={styles.th}>MESSAGE</th>
                      <th style={styles.th}>SUBMITTED</th>
                      <th style={styles.th}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rsvps.map((rsvp) => (
                      <tr key={rsvp._id} style={styles.tr}>
                        <td style={styles.td}>{rsvp.name}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.badge,
                            ...(rsvp.attendance === 'yes' ? styles.badgeYes : styles.badgeNo)
                          }}>
                            {rsvp.attendance === 'yes' ? '✓ Yes' : '✗ No'}
                          </span>
                        </td>
                        <td style={styles.td}>{rsvp.guests}</td>
                        <td style={styles.td}>{rsvp.message || '-'}</td>
                        <td style={styles.td}>
                          {new Date(rsvp.submittedAt).toLocaleDateString()}
                        </td>
                        <td style={styles.td}>
                          <button
                            onClick={() => deleteRSVP(rsvp._id)}
                            style={styles.deleteButton}
                          >
                          Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div style={styles.contentCard}>
            <h2 style={styles.cardTitle}>Guest Photos</h2>
            
            {photos.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyIcon}>📷</p>
                <p>No photos uploaded yet</p>
              </div>
            ) : (
              <div style={styles.photoGrid}>
                {photos.map((photo) => (
                  <div key={photo._id} style={styles.photoCard}>
                    <img
                      src={photo.imageUrl}
                      alt={photo.guestName}
                      style={styles.photoImage}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x250?text=Image+Not+Found';
                      }}
                    />
                    <div style={styles.photoInfo}>
                      <div style={styles.photoName}>👤 {photo.guestName}</div>
                      {photo.caption && (
                        <div style={styles.photoCaption}>"{photo.caption}"</div>
                      )}
                      {photo.approved ? (
                        <span style={styles.approvedBadge}>Approved</span>
                      ) : (
                        <span style={styles.pendingBadge}>Pending</span>
                      )}
                      <div style={styles.photoActions}>
                        {!photo.approved && (
                          <button
                            onClick={() => approvePhoto(photo._id)}
                            style={styles.approveButton}
                          >
                          Approve
                          </button>
                        )}
                        <button
                          onClick={() => deletePhoto(photo._id)}
                          style={styles.photoDeleteButton}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f5f5f5',
    paddingTop: '70px',
    paddingBottom: '3rem',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
    flexWrap: 'wrap',
  },
  headerLeft: {
    flex: '1',
    minWidth: '200px',
  },
  title: {
    fontSize: '2rem',
    color: WEDDING_CONFIG.colors.primary,
    margin: '0 0 0.8rem 0',
    fontFamily: "'Great Vibes', 'Playfair Display', serif",
  },
  subtitle: {
    fontSize: '1rem',
    color: '#666',
    margin: 0,
  },
  logoutButton: {
    padding: '0.9rem 1.8rem',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s',
    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)',
  },
  tabs: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '1rem 2rem',
    background: 'white',
    border: `2px solid ${WEDDING_CONFIG.colors.light}`,
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666',
    transition: 'all 0.3s',
    flex: '1',
    minWidth: '150px',
  },
  activeTab: {
    background: `linear-gradient(135deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 15px rgba(156, 72, 170, 0.3)',
    transform: 'translateY(-2px)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  statCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    textAlign: 'center',
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    transition: 'transform 0.3s',
  },
  statNumber: {
    fontSize: '3rem',
    fontWeight: '700',
    margin: '0.5rem 0',
  },
  statLabel: {
    fontSize: '0.85rem',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: 0,
    fontWeight: '600',
  },
  contentCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  cardTitle: {
    fontSize: '1.5rem',
    color: WEDDING_CONFIG.colors.primary,
    margin: 0,
    fontWeight: '600',
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  quickActionButton: {
    padding: '1.5rem',
    background: `linear-gradient(135deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    boxShadow: '0 4px 15px rgba(156, 72, 170, 0.3)',
  },
  quickActionIcon: {
    fontSize: '1.8rem',
  },
  quickActionText: {
    textAlign: 'left',
  },
  exportButton: {
    padding: '0.9rem 1.8rem',
    background: WEDDING_CONFIG.colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    boxShadow: '0 2px 8px rgba(156, 72, 170, 0.3)',
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    marginTop: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '900px',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    borderBottom: '2px solid #eee',
    color: '#333',
    fontWeight: '600',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: '#f8f9fa',
  },
  tr: {
    transition: 'background 0.2s',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #eee',
    color: '#666',
    fontSize: '0.95rem',
    verticalAlign: 'middle',
  },
  badge: {
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    display: 'inline-block',
  },
  badgeYes: {
    background: '#d4edda',
    color: '#155724',
  },
  badgeNo: {
    background: '#f8d7da',
    color: '#721c24',
  },
  deleteButton: {
    padding: '0.6rem 1.2rem',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    transition: 'background 0.3s',
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1.5rem',
    marginTop: '1.5rem',
  },
  photoCard: {
    background: '#f8f9fa',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s',
  },
  photoImage: {
    width: '100%',
    height: '280px',
    objectFit: 'cover',
  },
  photoInfo: {
    padding: '1.2rem',
  },
  photoName: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.5rem',
  },
  photoCaption: {
    fontSize: '0.9rem',
    color: '#666',
    marginBottom: '1rem',
    fontStyle: 'italic',
  },
  photoActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  approveButton: {
    flex: '1',
    padding: '0.7rem',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
  },
  photoDeleteButton: {
    flex: '1',
    padding: '0.7rem',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
  },
  approvedBadge: {
    padding: '0.5rem 1rem',
    background: '#d4edda',
    color: '#155724',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    display: 'inline-block',
  },
  pendingBadge: {
    padding: '0.5rem 1rem',
    background: '#fff3cd',
    color: '#856404',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
    display: 'inline-block',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.2rem',
    color: '#666',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#999',
    fontSize: '1.1rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    margin: '0 0 1rem 0',
  },
};

export default AdminDashboard;